import Swiper from "swiper";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import { gsap } from "gsap";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

/**
 * Animates the content of the active slide.
 * @param slide The DOM element of the active slide.
 */
const animateSlideContent = (slide: HTMLElement) => {
  // Select specific elements within this slide
  const elements = slide.querySelectorAll<HTMLElement>(
    ".hero-slider__title, .hero-slider__sub-title, .hero-slider__actions"
  );

  // Guard Clause: If no content elements exist, stop here to prevent GSAP warnings.
  if (elements.length === 0) {
    return;
  }

  // Reset state immediately before animating (prevents flash of old position)
  gsap.set(elements, { autoAlpha: 0, y: 40 });

  // Animate in
  gsap.to(elements, {
    autoAlpha: 1, // Handles opacity: 1 and visibility: inherit
    y: 0,
    duration: 0.8,
    stagger: 0.2, // 0.2s delay between each element (Title -> Sub -> Actions)
    ease: "power2.out",
    delay: 0.2, // Small pause after slide transition starts
  });
};

/**
 * Resets content of ALL slides to hidden.
 * This ensures that when we loop back to a slide, it is ready to animate in again.
 */
const resetAllSlides = (swiper: Swiper) => {
  const allElements = swiper.el.querySelectorAll<HTMLElement>(
    ".hero-slider__title, .hero-slider__sub-title, .hero-slider__actions"
  );

  // Guard Clause: If no content elements exist in the entire slider, stop.
  if (allElements.length === 0) {
    return;
  }

  gsap.set(allElements, { autoAlpha: 0, y: 40 });
};

export const initHeroSlider = (): void => {
  const sliders = document.querySelectorAll<HTMLElement>(
    ".hero-slider .swiper"
  );

  if (sliders.length === 0) {
    return;
  }

  sliders.forEach((sliderElement) => {
    new Swiper(sliderElement, {
      modules: [Navigation, Pagination, Autoplay, EffectFade],
      loop: true,
      effect: "fade",
      fadeEffect: {
        crossFade: true,
      },
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      on: {
        // Runs once when Swiper initializes
        init: (swiper) => {
          const activeSlide = swiper.slides[swiper.activeIndex];
          animateSlideContent(activeSlide);
        },
        // Runs every time the slide changes
        slideChangeTransitionStart: (swiper) => {
          // 1. Reset everyone first to ensure clean state
          resetAllSlides(swiper);

          // 2. Animate the new active slide
          const activeSlide = swiper.slides[swiper.activeIndex];
          animateSlideContent(activeSlide);
        },
      },
    });
  });

  console.log("[Slider] Hero Slider initialized with GSAP.");
};
